import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { CheckCircle, Briefcase, Calendar, Users, ArrowRight, X, CalendarCheck, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { organizationService } from '@/services/api/organization.service';

interface HowItWorksFlowProps {
  className?: string;
  /** Compact mode: smaller card for dashboard use */
  compact?: boolean;
  /** Allow user to dismiss/close the card (uses localStorage to remember) */
  dismissible?: boolean;
}

interface OnboardingStep {
  step: number;
  name: string;
  description: string;
  completed: boolean;
  required: boolean;
}

const STORAGE_KEY = 'sloty-how-it-works-dismissed';

/** Configuration for the 3 core onboarding steps */
const STEP_CONFIG: Record<string, {
  icon: typeof Briefcase;
  href: string;
  bgColor: string;
  iconColor: string;
  numberBg: string;
}> = {
  'Services Setup': {
    icon: Briefcase,
    href: '/admin/services',
    bgColor: 'bg-purple-100',
    iconColor: 'text-purple-600',
    numberBg: 'bg-purple-100 text-purple-700',
  },
  'Availability': {
    icon: Calendar,
    href: '/admin/availability',
    bgColor: 'bg-green-100',
    iconColor: 'text-green-600',
    numberBg: 'bg-green-100 text-green-700',
  },
  'Business Information': {
    icon: Users,
    href: '/admin/settings',
    bgColor: 'bg-brand-100',
    iconColor: 'text-brand-600',
    numberBg: 'bg-brand-100 text-brand-700',
  },
};

/** Steps to exclude from the checklist */
const EXCLUDED_STEPS = ['Account Creation', 'Team Setup'];

/** Static fallback steps used when the API is unavailable */
const STATIC_STEPS = [
  { id: 'services', icon: Briefcase, bgColor: 'bg-purple-100', iconColor: 'text-purple-600' },
  { id: 'availability', icon: Calendar, bgColor: 'bg-green-100', iconColor: 'text-green-600' },
  { id: 'bookings', icon: Users, bgColor: 'bg-brand-100', iconColor: 'text-brand-600' },
];

function SkeletonLoader({ compact }: { compact: boolean }) {
  return (
    <div className="animate-pulse">
      <div className={cn('h-4 bg-slate-200 rounded w-36', compact ? 'mb-3' : 'mb-4')} />
      <div className="h-1.5 bg-slate-200 rounded-full w-full mb-4" />
      <div className="space-y-3">
        <div className="h-10 bg-slate-200 rounded w-full" />
        <div className="h-10 bg-slate-200 rounded w-full" />
        <div className="h-10 bg-slate-200 rounded w-full" />
      </div>
    </div>
  );
}

function StaticFallback({ compact, t }: { compact: boolean; t: ReturnType<typeof useTranslation<'tips'>>['t'] }) {
  return (
    <>
      <div className={cn(
        'flex items-start justify-evenly',
        !compact && 'mb-6'
      )}>
        {STATIC_STEPS.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.id} className="contents">
              <div className={cn(
                'flex flex-col items-center text-center',
                compact ? 'w-20 md:w-24' : 'w-24 md:w-28'
              )}>
                <div className={cn(
                  'flex items-center justify-center rounded-lg',
                  compact ? 'w-10 h-10 md:w-12 md:h-12 mb-1.5' : 'w-12 h-12 mb-2',
                  step.bgColor
                )}>
                  <Icon className={cn(compact ? 'h-5 w-5 md:h-6 md:w-6' : 'h-6 w-6', step.iconColor)} />
                </div>
                <span className={cn(
                  'font-medium text-slate-900',
                  compact ? 'text-xs md:text-sm' : 'text-sm'
                )}>
                  {t(`howItWorks.steps.${step.id}.title`, step.id)}
                </span>
                <span className={cn(
                  'text-slate-500 mt-0.5',
                  compact ? 'text-[10px] md:text-xs' : 'text-xs'
                )}>
                  {t(`howItWorks.steps.${step.id}.subtitle`, '')}
                </span>
              </div>
              {index < STATIC_STEPS.length - 1 && (
                <div className={cn(
                  'flex items-center',
                  compact ? 'pt-2.5 md:pt-3' : 'pt-3.5'
                )}>
                  <ArrowRight className={cn(
                    'shrink-0',
                    compact
                      ? 'h-5 w-5 md:h-6 md:w-6 text-brand-400'
                      : 'h-5 w-5 text-slate-300'
                  )} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

export function HowItWorksFlow({ className, compact = false, dismissible = false }: HowItWorksFlowProps) {
  const { t } = useTranslation('tips');
  const [isDismissed, setIsDismissed] = useState(false);

  // Check localStorage on mount for dismissible mode
  useEffect(() => {
    if (dismissible) {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed === 'true') {
        setIsDismissed(true);
      }
    }
  }, [dismissible]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => organizationService.getOnboardingStatus(),
  });

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsDismissed(true);
  };

  // Don't render if dismissed
  if (dismissible && isDismissed) {
    return null;
  }

  // Extract and filter steps from API data
  const rawSteps = data?.onboarding?.steps as unknown as OnboardingStep[] | undefined;
  const steps = (rawSteps || []).filter(
    (step) => !EXCLUDED_STEPS.includes(step.name) && STEP_CONFIG[step.name]
  );
  const completedCount = steps.filter((s) => s.completed).length;
  const percentage = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;
  const allCompleted = steps.length > 0 && steps.every((s) => s.completed);

  // Auto-hide only if dismissed by user (not on completion — we show next steps instead)

  const hasApiData = !isLoading && !isError && steps.length > 0;

  return (
    <div className={cn(
      'bg-white rounded-lg border border-slate-200 relative',
      compact ? 'p-4' : 'p-6',
      className
    )}>
      {/* Dismiss button */}
      {dismissible && (
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
          aria-label={t('gettingStarted.dismiss', 'Dismiss')}
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Title */}
      <h2 className={cn(
        'font-semibold text-slate-900',
        compact ? 'text-sm md:text-base mb-3' : 'text-lg mb-4'
      )}>
        {t('gettingStarted.title', t('howItWorks.title', 'Getting Started'))}
      </h2>

      {/* Loading skeleton */}
      {isLoading && <SkeletonLoader compact={compact} />}

      {/* Error or no data: fall back to static display */}
      {!isLoading && !hasApiData && <StaticFallback compact={compact} t={t} />}

      {/* Checklist with real data */}
      {hasApiData && (
        <>
          {/* Progress bar */}
          <div className={cn('mb-4', compact && 'mb-3')}>
            <div className="flex items-center justify-between mb-1.5">
              <span className={cn(
                'text-slate-500',
                compact ? 'text-xs' : 'text-xs'
              )}>
                {t('gettingStarted.stepsComplete', '{{completed}} of {{total}} complete', { completed: steps.filter((s) => s.completed).length, total: steps.length })}
              </span>
              <span className={cn(
                'font-medium text-slate-600',
                compact ? 'text-xs' : 'text-xs'
              )}>
                {percentage}%
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>

          {/* Steps checklist */}
          <div className={cn('space-y-1', compact ? 'space-y-0.5' : 'space-y-1')}>
            {steps.map((step, index) => {
              const config = STEP_CONFIG[step.name];
              if (!config) return null;

              const Icon = config.icon;
              const stepNumber = index + 1;

              return (
                <Link
                  key={step.step}
                  href={config.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg transition-colors group',
                    compact ? 'px-2.5 py-2' : 'px-3 py-2.5',
                    'hover:bg-slate-50'
                  )}
                >
                  {/* Status indicator: checkmark or numbered circle */}
                  {step.completed ? (
                    <div className="flex items-center justify-center w-7 h-7 shrink-0">
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                    </div>
                  ) : (
                    <div className={cn(
                      'flex items-center justify-center w-7 h-7 rounded-full shrink-0 text-xs font-bold',
                      config.numberBg
                    )}>
                      {stepNumber}
                    </div>
                  )}

                  {/* Step content */}
                  <div className="flex-1 min-w-0">
                    <div className={cn(
                      'font-medium leading-tight',
                      compact ? 'text-xs md:text-sm' : 'text-sm',
                      step.completed ? 'text-slate-500' : 'text-slate-900'
                    )}>
                      {t(`gettingStarted.steps.${step.name}.title`, t(`progress.steps.${step.name}`, step.name))}
                    </div>
                    <div className={cn(
                      'leading-tight mt-0.5',
                      compact ? 'text-[10px] md:text-xs' : 'text-xs',
                      step.completed
                        ? 'text-emerald-500'
                        : 'text-slate-500'
                    )}>
                      {step.completed
                        ? t(`gettingStarted.steps.${step.name}.completedSubtitle`, 'Done')
                        : t(`gettingStarted.steps.${step.name}.subtitle`, step.description)
                      }
                    </div>
                  </div>

                  {/* Arrow for pending steps */}
                  {!step.completed && (
                    <ArrowRight className={cn(
                      'h-4 w-4 text-slate-300 shrink-0 group-hover:text-slate-500 transition-colors',
                    )} />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Post-completion: calendar integration suggestion */}
          {allCompleted && (
            <div className={cn('mt-3 pt-3 border-t border-slate-100')}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <span className={cn(
                  'font-medium text-emerald-600',
                  compact ? 'text-xs md:text-sm' : 'text-sm'
                )}>
                  {t('gettingStarted.complete')}
                </span>
              </div>
              <Link
                href="/admin/calendar-settings"
                className={cn(
                  'flex items-center gap-3 rounded-lg transition-colors group',
                  compact ? 'px-2.5 py-2' : 'px-3 py-2.5',
                  'bg-brand-50 hover:bg-brand-100'
                )}
              >
                <div className="flex items-center justify-center w-7 h-7 rounded-full shrink-0 bg-brand-100">
                  <CalendarCheck className="h-4 w-4 text-brand-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    'font-medium leading-tight text-slate-900',
                    compact ? 'text-xs md:text-sm' : 'text-sm'
                  )}>
                    {t('gettingStarted.nextStep.title')}
                  </div>
                  <div className={cn(
                    'leading-tight mt-0.5 text-slate-500',
                    compact ? 'text-[10px] md:text-xs' : 'text-xs'
                  )}>
                    {t('gettingStarted.nextStep.subtitle')}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-brand-400 shrink-0 group-hover:text-brand-600 transition-colors" />
              </Link>
            </div>
          )}
        </>
      )}

      {/* View all tips link - compact mode only */}
      {compact && (
        <div className={cn(
          'flex justify-center pt-3 border-t border-slate-100',
          hasApiData ? 'mt-3' : 'mt-3'
        )}>
          <Link
            href="/admin/tips"
            className="text-xs md:text-sm text-brand-600 hover:text-brand-700 font-medium"
          >
            {t('howItWorks.viewAllTips', 'View all tips')} →
          </Link>
        </div>
      )}
    </div>
  );
}
